// Module 07 — MEASUREMENT AUTO-GRADING, client side (spec 07 §3.4; M07-S09).
//
// The server owns the verdict. This file exists because the operator needs the
// answer BEFORE the round trip — §4.4 asks for "live in/out-of-range colouring
// against min/target/max" while a number is being keyed, and on the floor that
// round trip may be hours away. So the same rule is computed twice, and the two
// computations must agree exactly or the screen lies to somebody standing at a
// machine: it is `apps/api/src/modules/quality/grading.ts`'s `gradeMeasurement`,
// restated, with the same boundary semantics (inclusive both ends, a missing
// bound meaning unbounded on that side, signed values admitted).
//
// EXACTNESS (D-041), AND WHY IT IS NOT OPTIONAL HERE. `measured_value`,
// `min_value` and `max_value` are `numeric(18,6)` columns travelling as exact
// decimal TEXT. `Number("2.100000") <= Number("2.1")` happens to be true, but
// the general case is not: a factory's scrap decision on the exact boundary an
// operator is arguing about must not depend on binary floating point, and the
// server does not — it parses to a bigint scaled at 1e-6. This does the same,
// in ~20 lines, so the client's colour and the server's verdict cannot diverge
// on the one input where diverging is most expensive.
//
// THE ASYMMETRY (§3.4) IS THE MODULE'S FLAGSHIP CLAIM and lives in
// `overridableTo` below: a COMPUTED FAIL MAY NEVER BE SHOWN OR SENT AS A PASS.
// The screen renders no control for it (M07-S09 asserts the absence, not a
// disabled button), the server refuses it 422 `QUALITY_RESULT_OVERRIDE_FORBIDDEN`
// if one is ever synthesised, and this function is where the UI's half is
// decided so no screen has to re-derive it.

/** A recorded verdict on one check item (spec 07 §2.6). */
export type ResultValue = "pass" | "fail" | "na";

/** Spec 07 §2.3's four check types. */
export type CheckType = "pass_fail" | "measurement" | "photo" | "checklist";

/** The scale of every `numeric(18,6)` measurement column. */
const MEASUREMENT_SCALE = 6;

/**
 * Exact decimal text → an integer scaled at 1e-6, or `undefined` when the text
 * is not a decimal this contract admits.
 *
 * `undefined` rather than a throw, and rather than 0: this parses what a human
 * is TYPING, so "2." and "-" and "" are all normal intermediate states, not
 * errors. A caller that cannot parse shows no verdict at all — which is the
 * honest rendering of "there is not yet a number here" and is why the keypad
 * can colour nothing until there is something to colour.
 */
export function parseExactDecimal(text: string, scale = MEASUREMENT_SCALE): bigint | undefined {
  const trimmed = text.trim();
  if (!/^-?\d+(\.\d+)?$/u.test(trimmed)) return undefined;
  const negative = trimmed.startsWith("-");
  const digits = negative ? trimmed.slice(1) : trimmed;
  const [whole = "", fraction = ""] = digits.split(".");
  // More decimal places than the column holds is NOT roundable here: rounding
  // would show a verdict for a number the server never sees, and truncating
  // would show one for a number the operator never typed. Both are worse than
  // "no verdict yet"; the field's own validation says the value is too precise.
  if (fraction.length > scale) return undefined;
  const scaled = BigInt(whole + fraction.padEnd(scale, "0"));
  return negative ? -scaled : scaled;
}

/** True when `text` is a decimal this wire accepts at `numeric(18,6)`. */
export function isExactMeasurement(text: string): boolean {
  return parseExactDecimal(text) !== undefined;
}

/** The bounds of one measurement item — either may be absent ("unbounded"). */
export interface MeasurementBounds {
  readonly min_value: string | null;
  readonly max_value: string | null;
}

/** Where a keyed measurement sits relative to its bounds. */
export type MeasurementBand =
  /** No number yet, or not a decimal this wire accepts. */
  "unknown" | "below" | "in_range" | "above";

export function bandOf(text: string, bounds: MeasurementBounds): MeasurementBand {
  const v = parseExactDecimal(text);
  if (v === undefined) return "unknown";
  if (bounds.min_value !== null) {
    const min = parseExactDecimal(bounds.min_value);
    if (min !== undefined && v < min) return "below";
  }
  if (bounds.max_value !== null) {
    const max = parseExactDecimal(bounds.max_value);
    if (max !== undefined && v > max) return "above";
  }
  return "in_range";
}

/**
 * THE COMPUTED VERDICT (§3.4), or `undefined` when there is no number to grade.
 * Pass iff `min ≤ v ≤ max`, inclusive at both ends, a missing bound meaning
 * unbounded on that side — the server's own arithmetic.
 */
export function gradeMeasurement(
  text: string,
  bounds: MeasurementBounds,
): "pass" | "fail" | undefined {
  const band = bandOf(text, bounds);
  if (band === "unknown") return undefined;
  return band === "in_range" ? "pass" : "fail";
}

/**
 * WHICH VERDICTS THE SCREEN MAY OFFER for one check item (§3.4's asymmetry).
 *
 * For a MEASUREMENT the instrument has already answered, and the only question
 * left is whether the operator's eye disagrees in the direction that ADDS
 * information:
 *   computed fail → `["fail"]`         a graded fail is a fail. No control
 *                                      exists to say otherwise (M07-S09), which
 *                                      is why this returns a one-element list
 *                                      rather than a disabled Pass button —
 *                                      D-118: absence, not a dead control whose
 *                                      reason nobody can read.
 *   computed pass → `["pass", "fail"]` a bubble in the sheath the micrometer
 *                                      cannot see. §3.4 asks for a note with it.
 *   no number yet → `[]`               nothing to record.
 *
 * Every other check type is the operator's judgement outright, and an OPTIONAL
 * item may additionally be skipped `na` (§2.6 — a required item may not, and
 * completion refuses one that is, §3.4).
 */
export function offeredVerdicts(input: {
  checkType: CheckType;
  bounds: MeasurementBounds;
  measuredText: string;
  isRequired: boolean;
}): ResultValue[] {
  if (input.checkType === "measurement") {
    const computed = gradeMeasurement(input.measuredText, input.bounds);
    if (computed === undefined) return [];
    return computed === "fail" ? ["fail"] : ["pass", "fail"];
  }
  return input.isRequired ? ["pass", "fail"] : ["pass", "fail", "na"];
}

/**
 * The verdict to SEND for a measurement, given what the operator picked.
 *
 * `undefined` means "send no `result` at all and let the server compute it",
 * which is the contract's own default (`result` is optional on a measurement
 * request) and the one path that cannot desynchronise the two graders. An
 * explicit verdict is only sent when the operator overrode a computed pass down
 * to a fail — the direction §3.4 admits.
 */
export function verdictToSend(
  picked: ResultValue | undefined,
  computed: "pass" | "fail" | undefined,
): ResultValue | undefined {
  if (picked === undefined) return undefined;
  if (computed === undefined) return picked;
  if (picked === computed) return undefined;
  // The forbidden direction never leaves this function, whatever a caller
  // passed: the screen offers no control for it, and if one were ever
  // introduced the request would still not carry it.
  if (picked === "pass" && computed === "fail") return undefined;
  return picked;
}

/**
 * §3.4's completion gate, client side: which REQUIRED items still have no
 * verdict, and which still owe evidence (§9.8 — photos are checked at
 * completion, not at recording, which is what makes a partial offline sync
 * finishable).
 *
 * The server decides; this exists so the Complete button can say WHY it is dead
 * (D-118) instead of being a control that does nothing.
 */
export interface CompletionItemState {
  readonly checkItemId: string;
  readonly name: string;
  readonly isRequired: boolean;
  readonly result: ResultValue | null;
  readonly photoRequired: boolean;
  readonly minPhotos: number;
  readonly photoCount: number;
}

export interface CompletionReadiness {
  readonly ready: boolean;
  /** Required items with no verdict, or verdict `na` (§3.4 admits neither). */
  readonly missing: readonly CompletionItemState[];
  /** Items whose evidence bar is unmet (§2.3 `photo_required` / `min_photos`). */
  readonly missingEvidence: readonly CompletionItemState[];
  /** True when at least one REQUIRED item is `fail` — §3.4 makes the failure
   * cause mandatory in exactly that case, and the screen asks for one. */
  readonly willFail: boolean;
}

export function completionReadiness(items: readonly CompletionItemState[]): CompletionReadiness {
  const missing = items.filter(
    (item) => item.isRequired && (item.result === null || item.result === "na"),
  );
  const missingEvidence = items.filter(
    (item) =>
      item.photoRequired &&
      item.result !== null &&
      item.result !== "na" &&
      item.photoCount < item.minPhotos,
  );
  const willFail = items.some((item) => item.isRequired && item.result === "fail");
  return {
    ready: missing.length === 0 && missingEvidence.length === 0,
    missing,
    missingEvidence,
    willFail,
  };
}
